/**
 * Answer comparison for Gramática.
 *
 * Like Grammatik, this deliberately does NOT use `isCorrectAnswer` from
 * `@/core/normalize`: Levenshtein tolerance applied to grammar marks both sides
 * of a distinction correct and the deck stops measuring anything. Portuguese
 * has its own minimal pairs to protect — `e`/`é`, `esta`/`está`, `avô`/`avó`,
 * `pais`/`país`, `pode`/`pôde` — and every one of them is a single edit apart.
 *
 * WHERE THIS DIFFERS FROM GERMAN, AND WHY
 *
 * German transliterates its umlauts and never folds them away, because a
 * missing umlaut is usually the answer being wrong. Portuguese does the
 * opposite by default, and the reasoning is about the keyboard rather than the
 * language.
 *
 * The first draft of the plan forbade accent forgiveness entirely, on the
 * grounds that the pairs above are meaning-bearing. That is right about
 * Portuguese and wrong about a phone: a learner typing on a German keyboard has
 * no `ã` and no `ç`, and failing her for that teaches frustration rather than
 * orthography. She is learning where the accent goes by reading it on the card,
 * not by being marked wrong for lacking a key.
 *
 * So the default accepts the unaccented form and the card back shows what the
 * accented one was. An exercise whose whole subject IS the distinction sets
 * `accent_sensitive` and gets the strict comparison. That is the same shape as
 * `case_sensitive` in the German schema, which exists for the same reason:
 * everything should forgive the thing that one lesson is about.
 *
 * Case is folded always, with no opt-out. Portuguese does not capitalise its
 * nouns and no lesson in this course is about capitalisation, so there is
 * nothing for a strict mode to protect.
 */
import {
  isGrammarAnswerCorrect as sharedIsGrammarAnswerCorrect,
  isOrderCorrect as sharedIsOrderCorrect,
} from '../shared/grammar-answer';

/**
 * Every accented character Portuguese uses, and the bare letter under it.
 *
 * Written out rather than done with `normalize('NFD')` and a combining-mark
 * strip. The Unicode route also folds accents this table deliberately leaves
 * alone in other scripts, and — more to the point — it is unreadable at the
 * call site. A reader of this file should be able to see exactly which
 * characters are forgiven, because that set is a product decision.
 */
const STRIP_ACCENT: Record<string, string> = {
  á: 'a',
  à: 'a',
  â: 'a',
  ã: 'a',
  é: 'e',
  ê: 'e',
  í: 'i',
  ó: 'o',
  ô: 'o',
  õ: 'o',
  ú: 'u',
  ç: 'c',
};

/**
 * Normalize one side of a grammar comparison.
 *
 * Forgives, in order: surrounding whitespace, repeated internal whitespace, a
 * single trailing sentence period, case, and — unless the exercise opts out —
 * accents.
 *
 * Does not forgive: word order, missing words, wrong endings, or any
 * punctuation other than that one trailing period. It also does not forgive a
 * missing question mark being added, or vice versa, because lesson 19 is about
 * questions being marked by intonation alone and the written mark is the only
 * trace of that in text.
 */
export function normalizeGrammarAnswer(text: string, accentSensitive = false): string {
  let out = text.trim().replace(/\s+/g, ' ').replace(/\.$/, '').trim().toLowerCase();
  if (!accentSensitive) {
    out = [...out].map((char) => STRIP_ACCENT[char] ?? char).join('');
  }
  return out;
}

/**
 * Membership and order comparison come from the shared module — see its header.
 * Bound to this normalizer so callers in the Portuguese app import from here.
 */
export function isGrammarAnswerCorrect(
  input: string,
  accepted: string[],
  accentSensitive = false,
): boolean {
  return sharedIsGrammarAnswerCorrect(normalizeGrammarAnswer, input, accepted, accentSensitive);
}

export function isOrderCorrect(input: string[], accepted: string[][]): boolean {
  return sharedIsOrderCorrect(normalizeGrammarAnswer, input, accepted);
}

/**
 * The accented form `input` was probably reaching for, or null.
 *
 * Drives the card back's "close — it is *está*, with the accent" line. Only
 * meaningful when the answer was accepted BECAUSE accents were folded; a
 * correct accented answer returns null and the card says nothing.
 */
export function accentNear(input: string, accepted: string[]): string | null {
  const loose = normalizeGrammarAnswer(input);
  const exact = normalizeGrammarAnswer(input, true);
  const match = accepted.find((candidate) => normalizeGrammarAnswer(candidate) === loose);
  if (!match) return null;
  return normalizeGrammarAnswer(match, true) === exact ? null : match;
}
